(ns frontend.view.grid.surveys-grid.events
  (:require
   [ajax.core :as ajax]
   [common.date-formats :as date-formats]
   [common.localization :as loc]
   [common.schema.entity-types :as entity-types]
   [common.schema.registry :as registry]
   [common.timezones :as timezones]
   [common.util :as cu]
   [frontend.actions.named-view :as named-view-actions]
   [frontend.conn :as conn]
   [frontend.controller.websocket.event :as ws]
   [frontend.exports.excel :as excel]
   [frontend.exports.exceljs.workbook :as ex-workbook]
   [frontend.local-storage :as ls]
   [frontend.logging :as logging]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.utils :as fu]
   [frontend.view.grid.investors-grid.events :as investor-events]
   [frontend.view.grid.surveys-grid.columns :as columns]
   [frontend.view.grid.surveys-grid.db :as db]
   [frontend.view.grid.surveys-grid.mass-remind.event :as mass-remind-event]
   [frontend.permissions.survey-mass-sending :as mass-sending]
   [frontend.view.modals.survey-cycle-actions.view :as survey-cycle-actions-modal]
   [frontend.view.grid.surveys-grid.sub :as sub]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.events :as modals]
   [frontend.zap-time :as zap-time]
   [promesa.core :as p]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.fx.toast :as toast]))

(defn ls-path
  [& rest]
  (concat
   [(:db/id @session/account)
    (:db/id @session/tenant)
    :survey-profiles-view-settings]
   rest))

(defn precalculated-schema
  [recalculate?]
  (let [calculated @db/schema
        tenant-schema @session/tenant-schema
        schema
        (cond
          (and (not recalculate?)
               calculated)
          calculated
          :else
          (->>
           (registry/get-schema @session/tenant-schema :entity.type/survey-profile)
           (remove
            (fn [[k v]]
              (let [grid-visibility (->> v
                                         :field/default-visibility
                                         :grid)]
                (or (nil? grid-visibility)
                    (= grid-visibility :editor.type/none)
                    (if (not= (:field/data-type v) :data.type/currency-symbol)
                      (not (f-permissions/field-read-access? k :entity.type/survey-profile nil))
                      (not (f-permissions/field-read-access? (:field/related-fields v)
                                                             :entity.type/survey-profile
                                                             nil)))))))
           (sort
            (fn [[attribute-a _] [attribute-b _]]
              (cond                                                                                 ;; show name first
                (= :survey-profile/name attribute-a)
                -1
                (= :survey-profile/name attribute-b)
                1
                :else
                (compare (let [fs (registry/get-field-schema tenant-schema attribute-a)]
                           (loc/field-name attribute-a fs))
                         (let [fs (registry/get-field-schema tenant-schema attribute-b)]
                           (loc/field-name attribute-b fs))))))
           (doall)))
        investor-schema
        (->> (investor-events/precalculated-schema recalculate?)
             (map (fn [[k v]]
                    [(if (vector? k)
                       (into [:survey-profile/target] k)
                       [:survey-profile/target k]) v]))
             (sort-by (fn [[field-path _]]
                        (let [k (last field-path)]
                          (let [fs (registry/get-field-schema tenant-schema k)]
                            (loc/field-name k fs)))))
             (doall))
        schema (if (and (not recalculate?)
                        calculated)
                 schema
                 (concat schema investor-schema))]
    (when (or recalculate?
              (not calculated))
      (reset! db/schema schema))
    schema))

;;precalculate for performance reasons
(defn keys-and-schemas
  []
  (precalculated-schema false))

(defn survey-profiles-column-keys
  []
  (let [schemas (keys-and-schemas)
        col-keys (concat
                  (when (session/is-admin?) [:db/id])
                  (map first schemas))]
    col-keys))

(def default-kanban-attributes [:survey-profile/name :survey-profile/status])

(defn visible-keys-and-schemas
  []
  (let [{:keys [attributes footer-1 footer-2]} @db/card-settings
        visible-attributes
        (set (concat @db/selected-columns
                     attributes
                     default-kanban-attributes
                     [footer-1 footer-2
                      (entity-types/type->owner-attribute :entity.type/survey-profile)]))]
    (->> (keys-and-schemas)
         (filter (fn [[k _fs]]
                   (some #{k} visible-attributes))))))

(declare expanded? toggle-expand!)

;;necessary arguments to use common grid functions
(defn setup-args
  []
  {:state                       db/survey-profiles
   :entity-type                 :entity.type/survey-profile
   :selected-columns-state      db/selected-columns
   :ordered-columns-state       db/ordered-columns
   :filters-state               db/survey-profile-filters
   :view-settings-state         db/view-settings
   :column-widths-state         db/column-widths
   :selected-pipeline           nil
   :ls-path                     ls-path
   :kanban?                     true
   :kanban-args                 {:default-grouped-by           :survey-profile/status
                                 :kanban-state                 db/kanban
                                 :grouped-by-state             db/grouped-by
                                 :card-settings-state          db/card-settings
                                 ;;for long card view:
                                 :default-attributes           default-kanban-attributes
                                 :expanded-state               db/expanded
                                 :sorted-kanban-entities-state db/sorted-kanban-entities
                                 :lists-state                  db/lists
                                 :expanded?                    expanded?
                                 :toggle-expand!               toggle-expand!}
   :column-keys                 survey-profiles-column-keys
   :default-columns             columns/default-columns
   :mandatory?                  columns/mandatory?
   :visible-keys-and-schemas-fn visible-keys-and-schemas})

(defn expanded?
  [entity-id]
  (let [card-mode (or (:card-mode @db/kanban)
                      :short-card)
        individual (get @db/expanded entity-id)]
    (or (and (= card-mode :short-card)
             individual)
        (and (= card-mode :long-card)
             (not individual)))))

(defn toggle-expand!
  [entity-id]
  (let [expanded? (get @db/expanded entity-id)]
    (if expanded?
      (swap! db/expanded dissoc entity-id)
      (swap! db/expanded assoc entity-id true))))

(defn resolve-column-key-universe
  []
  (grid/resolve-column-key-universe (setup-args)))

(defn resolve-column-keys
  []
  (grid/resolve-column-keys (setup-args)))

(defn cleanup
  []
  (when-let [tracker (:tracker-1 @db/survey-profiles)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-2 @db/survey-profiles)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-3 @db/survey-profiles)]
    (r/dispose! tracker))

  (swap! db/survey-profiles dissoc
         :loaded
         :schema
         :entities
         :entity-types
         :view-settings
         :sorted-grid-entities
         :search
         :kanban
         :sorted-kanban-entities
         :selected
         :grid-entities))

(defn toggle-quick-filters!
  []
  (swap! db/survey-profiles update :show-quick-filters? not))

(defn system-views
  []
  (concat [{:named-view/settings {:selected-columns columns/default-columns
                                  :ordered-columns  columns/ordered-default-columns
                                  :sort-by          :asset/name
                                  :sort-direction   "ASC"
                                  :view-mode        (if (tenant-customizations/capman?)
                                                      :grid-mode
                                                      :board-mode)
                                  :grouped-by       :survey-profile/status}
            :named-view/name     (loc/tr :all-survey-profiles)
            :disable-delete?     true
            :db/id               "named-view-all-survey-profiles"}]
          (when-not (tenant-customizations/capman?)
            [{:named-view/settings {:selected-columns columns/default-columns
                                    :ordered-columns  columns/ordered-default-columns
                                    :sort-by          :asset/name
                                    :sort-direction   "ASC"
                                    :view-mode        :grid-mode}
              :named-view/name     (loc/tr :list-survey-profiles)
              :disable-delete?     true
              :db/id               "named-view-list-survey-profiles"}])
          [{:named-view/settings {:selected-columns columns/default-columns
                                  :sort-by          :asset/name
                                  :sort-direction   "ASC"
                                  :view-mode        :grid-mode
                                  :filters          {:survey-profile/our-contact
                                                     {:operand           :operand/any-of
                                                      :lower-bound-value #{(:db/id
                                                                            @session/account)}}}}
            :named-view/name     (loc/tr :my-survey-profiles)
            :disable-delete?     true
            :db/id               "named-view-my-survey-profiles"}]))

(defn system-views-by-id
  []
  (->> (system-views)
       (map (fn [v]
              [(:db/id v) v]))
       (into {})))

(defn named-views
  []
  (concat (system-views)
          (->> @session/all-named-views
               (cu/only-entity :named-view/view :surveys-grid)
               (sort-by :common/created-on))))

(defn named-view-by-id
  [id]
  (or (get @session/entities id)
      (get (system-views-by-id) id)))

(defn switch-mode!
  [current-mode]
  (let [new-mode (if (= current-mode :grid-mode) :board-mode :grid-mode)]
    (swap! db/view-settings assoc :view-mode new-mode)
    (ls/update-in (ls-path) assoc :view-mode new-mode)))

(defn setup-column-state!
  [view-settings]
  (reset! db/view-settings view-settings)
  (when (nil? @db/selected-columns)
    (let [default-view-settings (->> (named-views)
                                     first
                                     :named-view/settings)]
      (reset! db/view-settings default-view-settings)
      (ls/set-in (ls-path) default-view-settings)))
  (grid/recalculate (assoc (setup-args) :re-sort? true)))

(defn load-view!
  [named-view]                                                                                      ;;named-view entity
  (let [{:keys [named-view/settings]} named-view]
    (ls/set-in (ls-path) settings)
    (setup-column-state! settings)))

(defn permission-updates
  []
  (let [current-permissions @session/current-permissions]
    (when current-permissions
      (precalculated-schema true))))

(defn trigger-recalculation
  []
  (let [permissions-trigger @db/schema
        entities-trigger @db/grid-entities]
    (when (or permissions-trigger entities-trigger)
      (fu/timeout 10
                  (fn []
                    (grid/recalculate (assoc (setup-args) :stale-sort? true))
                    (swap! db/survey-profiles assoc :loaded true)
                    (deref db/survey-profiles))))))

(defn setup
  [closed-atom?]
  (when-not @closed-atom?
    (if (empty? (ls/get-in (ls-path)))
      (load-view! (first (named-views)))
      (reset! db/view-settings (ls/get-in (ls-path))))
    (swap! db/survey-profiles assoc
           :tracker-2 (r/track! permission-updates)
           :tracker-3 (r/track! trigger-recalculation))
    (setup-column-state! (ls/get-in (ls-path)))))

(defn save-current-view!
  [name]
  (let [settings @db/view-settings
        msg {:named-view/name     name
             :named-view/view     :surveys-grid
             :named-view/settings settings}]
    (named-view-actions/*create-named-view->
     msg
     identity
     (fn [& a]                                                                                      ;;TODO show error message
       (logging/error "create-named-view failed" a)))))

(defn target-asset-in
  [company-id]
  (let [target-asset-oppos (->> @session/all-oppos
                                (remove :common/disabled?)
                                (cu/only-entity :oppo/target-asset company-id))
        target-asset-holdings (->> @session/all-holdings
                                   (remove :common/disabled?)
                                   (cu/only-entity :holding/target-asset company-id))
        all-target-asset-entities (concat target-asset-oppos
                                          target-asset-holdings)]
    all-target-asset-entities))

(defn mass-delete!
  []
  (-> (conn/sendp! :survey-profile/mass-action
                   {:action             :delete-survey-profiles
                    :survey-profile-ids (grid/get-selected-items (setup-args))})
      (p/then (fn [_resp]
                (swap! db/survey-profiles assoc :selected {})
                (rf/dispatch [::reload])))
      (p/catch (fn [e]
                 (logging/error "Unable to delete selected survey-profiles" e)))))

(defn delete-selected-clicked
  [_]
  (modals/open-modal!
   confirmation/modal-key
   {:body          [:div
                    [:p (loc/tr :confirm-delete-selected)]]
    :confirm-text  (loc/tr :confirm-delete-selected-button)
    :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
    :on-confirm    #(mass-delete!)}))

(defn mass-remind-clicked
  []
  (if (mass-sending/enabled?)
    ;; Rule-driven reminders live on the same screen as the invitations, one
    ;; tab over. Due reminders are derived per cycle and batch and have no
    ;; profile scoping, so the selection does not narrow them - but it is
    ;; still carried, because the other tab is one click away and is where a
    ;; selection means something.
    (rf/dispatch [::mass-remind-v3 (grid/get-selected-entities (setup-args))])
    (let [selected-ids (grid/get-selected-items (setup-args))]
      (rf/dispatch [::mass-remind-event/init selected-ids]))))

(rf/reg-event-fx
 ::mass-remind-v3
 (fn [{db :db} [_ selected-profiles]]
   (modals/open-modal! survey-cycle-actions-modal/modal-key
                       {:survey-type-id (get-in db [::survey-type-id])
                        :profiles       selected-profiles
                        :active-tab     :reminders})
   {}))

(rf/reg-event-fx
 ::start-download!-ok
 (fn [{db :db} [_ resp]]
   (let [body (:body resp)]
     (aset js/window.location "href" (:download-url body))
     {:db (dissoc db ::sub/downloading?)})))

(rf/reg-event-fx
 ::start-download!-failed
 (fn [{db :db} [_ resp]]
   {:db (dissoc db ::sub/downloading?)}))

(rf/reg-event-fx
 ::start-download!
 (fn [{db :db} [_ selected-ids]]
   {:db       (assoc db ::sub/downloading? true)
    ::ws/send {:id         :survey-profile/download-attachments!
               :body       {:profile-ids selected-ids}
               :on-success [::start-download!-ok]
               :on-error   [::start-download!-failed]}}))

(defn download-clicked
  []
  (let [selected-ids (grid/get-selected-items (setup-args))]
    (js/console.log "Download : " selected-ids)
    (rf/dispatch [::start-download! selected-ids])))

(rf/reg-event-fx
 ::mass-send-init
 (fn [{db :db} [_ selected-profiles]]
   (if (empty? selected-profiles)
     {::toast/info {:label (loc/tr :survey-mass-send-select-profiles)}}
     (do
       ;; The same screen settings opens on a cycle. The grid supplies the
       ;; recipients and leaves the cycle to be picked; settings does the
       ;; reverse. There is no second implementation of what happens after
       ;; that.
       (modals/open-modal! survey-cycle-actions-modal/modal-key
                           {:profiles       selected-profiles
                            :survey-type-id (get-in db [::survey-type-id])})
       {}))))

(defn mass-send-clicked
  []
  (rf/dispatch [::mass-send-init (grid/get-selected-entities (setup-args))]))

(defn expand-all!
  []
  (swap! db/kanban assoc :card-mode :long-card)
  (reset! db/expanded {}))

(defn collapse-all!
  []
  (swap! db/kanban assoc :card-mode :short-card)
  (reset! db/expanded {}))

(defn open-card-settings!
  []
  (swap! db/kanban assoc :card-settings? true))

(defn close-card-settings!
  []
  (swap! db/kanban dissoc :card-settings?))

(defn approved-process?
  [process]
  (contains? #{:survey-process.status/approved :survey-process.status/approved-modified}
             (:survey-process/status process)))

(defn remove-attachment-questions
  [questions-meta]
  (->> questions-meta
       (remove (fn [[_q question-meta]]
                 (contains? #{:data.type/file :data.type/confirm} (:data/type question-meta))))))

(defn get-export-sheet-names-data-and-column-keys
  "Returns list of [sheet-name data-rows sorted-column-keywords]"
  [tenant tenant-schema export-schemas keys-and-schemas profiles]
  (let [profile-datas
        (->>
         profiles
         (map
          (fn [profile]
            (merge
             profile
             (->> keys-and-schemas
                  (map
                   (fn [[k fs]]
                     [k
                      (fu/display-data tenant-schema tenant k fs profile :skip-multiplier? true)]))
                  (into {}))))))
        column-keys (->> keys-and-schemas
                         (map first))]
    (concat
     [["survey profiles" profile-datas column-keys]])))

(defn process-next-sheet!
  [{:keys [wb export-entities timer
           export-sheet-datas-atom progress-atom progress-rows-atom cancel-atom]
    :as   args}]
  (let [next-sheet (first @export-sheet-datas-atom)]
    (if-not next-sheet
      (let [todays-date (date-formats/format-millis
                         (zap-time/now)
                         (or (:tenant/date-format @session/tenant) (loc/tr :date-format))
                         (or (:tenant/timezone @session/tenant) timezones/default-timezone))
            file-name (loc/tr :export-filename-generic [(loc/tr :entity.type/survey-profile) todays-date])]
        (ex-workbook/save-workbook wb file-name)
        (reset! progress-atom 1))
      (let [[sheet-name sheet-data sheet-columns] next-sheet]
        (reset! export-sheet-datas-atom (rest @export-sheet-datas-atom))
        (excel/export-sheet-entities
         (assoc args
           :skip-progress-modal? true
           :processed-rows-start-count @progress-rows-atom
           :on-sheet-completed! (fn []
                                  (fu/timeout 150
                                              (fn []
                                                (process-next-sheet! args))))
           :sheet (ex-workbook/add-sheet wb sheet-name)
           :column-attributes sheet-columns
           :data-map-rows sheet-data))))))

(rf/reg-event-fx
 ::on-search-ok
 (fn [{:keys [db] :as cofx} [_ resp]]
   (reset! db/grid-entities (:body resp))
   {}))

(rf/reg-event-fx
 ::on-search-failed
 (fn [{:keys [db] :as cofx} [_ resp]]
   (js/console.log "RESP failed : " resp)
   {}))

(rf/reg-event-fx
 ::reload
 (fn [{:keys [db] :as cofx} [_ survey-type-id]]
   (let [survey-type-id (or survey-type-id
                            (get-in db [::survey-type-id]))]
     {:db         (-> db
                      (assoc-in [::survey-type-id] survey-type-id))
      :http-xhrio {:method          :post
                   :uri             "/surveys/api/search-profiles"
                   :params          {:filters {:survey-type-id survey-type-id}}
                   :timeout         30000
                   :format          (ajax/transit-request-format)
                   :response-format (ajax/transit-response-format)
                   :on-success      [::on-search-ok]
                   :on-failure      [::on-search-failed]}})))
